import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

interface ParetoData {
  process: string;
  deviations: number;
  status: 'critical' | 'warning' | 'healthy';
}

const mockData: ParetoData[] = [
  { process: 'Montagem Final', deviations: 45, status: 'critical' },
  { process: 'Logística Inbound', deviations: 32, status: 'critical' },
  { process: 'Usinagem CNC', deviations: 28, status: 'warning' },
  { process: 'Inspeção de Qualidade', deviations: 15, status: 'healthy' },
  { process: 'Embalagem Térmica', deviations: 5, status: 'healthy' },
].sort((a, b) => b.deviations - a.deviations);

export function BarChartBlueprints() {
  const navigate = useNavigate();
  const [selectedProcess, setSelectedProcess] = useState<string | null>(null);

  const handleBarClick = (data: any) => {
    // Toggle selection
    if (selectedProcess === data.process) {
      setSelectedProcess(null);
    } else {
      setSelectedProcess(data.process);
      
      // If critical or warning, we could theoretically open a drawer here.
      // For now, we simulate opening the context or navigating.
      if (data.status === 'critical' || data.status === 'warning') {
        console.log(`[Drill-down] Opening Drawer for ${data.process}`);
      }
    }
  };

  const handleInvestigateMASP = () => {
    if (!selectedProcess) return;
    // Navigate to Issues page with a pre-filled filter (assuming ?process=... is handled there)
    navigate(`/problemas?process=${encodeURIComponent(selectedProcess)}`);
  };
  const getFillColor = (status: string) => {
    switch (status) {
      case 'critical': return 'url(#colorCritical)';
      case 'warning': return 'url(#colorWarning)';
      case 'healthy': return 'url(#colorHealthy)';
      default: return '#94a3b8';
    }
  };

  return (
    <div className="rounded border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800/60 dark:bg-slate-900/40 dark:backdrop-blur-sm">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h3 className="text-[15px] font-extrabold uppercase tracking-wide text-slate-800 dark:text-slate-100">
            Pareto de Desvios por Processo
          </h3>
          <p className="mt-1 text-[12px] font-medium tracking-wide text-slate-500">
            Críticos vs. Saudáveis (Andon View)
          </p>
        </div>
        {selectedProcess && (
          <button
            onClick={handleInvestigateMASP}
            className="flex items-center gap-2 rounded bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-600 transition-colors hover:bg-red-500/20 dark:bg-red-500/20 dark:text-red-400 dark:hover:bg-red-500/30 border border-red-500/30"
          >
            <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            Abrir Investigação MASP
          </button>
        )}
      </div>
      
      <div className="h-[340px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={mockData} margin={{ top: 10, right: 10, left: -20, bottom: 85 }}>
            <defs>
              <linearGradient id="colorCritical" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f43f5e" stopOpacity={1} />
                <stop offset="100%" stopColor="#be123c" stopOpacity={0.8} />
              </linearGradient>
              <linearGradient id="colorWarning" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={1} />
                <stop offset="100%" stopColor="#b45309" stopOpacity={0.8} />
              </linearGradient>
              <linearGradient id="colorHealthy" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                <stop offset="100%" stopColor="#047857" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            
            <CartesianGrid 
              strokeDasharray="3 3" 
              vertical={false} 
              stroke="#1e293b" 
              strokeOpacity={0.5} 
            />
            
            <XAxis 
              dataKey="process" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#64748b', fontSize: 10, fontWeight: 600 }} 
              angle={-45}
              textAnchor="end"
              dy={15}
              interval={0}
              tickFormatter={(value) => value.toUpperCase()}
            />
            
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#64748b', fontSize: 10, fontWeight: 600 }} 
            />
            
            <Tooltip 
              cursor={{ fill: 'rgba(30, 41, 59, 0.2)' }}
              contentStyle={{ 
                backgroundColor: 'rgba(15, 23, 42, 0.75)', 
                backdropFilter: 'blur(12px)',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#f8fafc',
                fontSize: '12px',
                fontWeight: 600,
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              itemStyle={{ color: '#e2e8f0', fontWeight: 'bold' }}
              formatter={(value: number) => [`${value} desvios`, 'Registros']}
            />
            
            <Bar 
            dataKey="deviations" 
            barSize={30} 
            radius={[10, 10, 0, 0]}
            onClick={handleBarClick}
            cursor="pointer"
          >
            {mockData.map((entry, index) => {
              const isSelected = selectedProcess === entry.process;
              const isFaded = selectedProcess !== null && !isSelected;
              return (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getFillColor(entry.status)} 
                  className="transition-all duration-300"
                  style={{ 
                    opacity: isFaded ? 0.3 : 1,
                    filter: isSelected ? 'drop-shadow(0 0 8px rgba(255,255,255,0.3))' : 'none'
                  }}
                />
              );
            })}
          </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
